import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  runSummaryAccountSchema,
  runSummaryCountsSchema,
} from "@erp/workflows";

/** How far back the failed-runs popover looks, and the most runs it lists. */
export const VENDOR_PORTAL_FAILED_RUNS_DAYS = 30;
export const MAX_VENDOR_PORTAL_FAILED_RUNS = 20;

/**
 * A Vendor Portal run whose Run Summary names at least one account needing attention
 * (a Failed Account, or one with no Price Lists), read from its Email Run Summary step.
 */
export const VendorPortalFailedRunSchema = z.object({
  workflowId: z.uuid(),
  workflowRunId: z.uuid(),
  /** The name of the workflow that ran, which is the vendor. */
  vendor: z.string(),
  /** When the Email Run Summary step reported the run. */
  reportedAt: z.coerce.date(),
  /** Who started the run; null for a system-triggered run. */
  startedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  counts: runSummaryCountsSchema,
  /** Every account the Run Summary lists, in file order. */
  accounts: z.array(runSummaryAccountSchema),
  /** The Run Summary CSV, downloadable like any Vendor Portal file. */
  summary: z.object({ documentId: z.uuid(), fileName: z.string() }).nullable(),
});

export const ListVendorPortalFailedRunsOutputSchema = z.object({
  /** Newest first, from the last 30 days, at most 20; started by anyone in the organization. */
  items: z.array(VendorPortalFailedRunSchema),
  /** Failed runs reported since the caller last opened the popover. */
  unseenCount: z.number().int().nonnegative(),
});

export const vendorPortalsContracts = {
  listFailedRuns: oc.output(ListVendorPortalFailedRunsOutputSchema).route({
    method: "GET",
    summary: "List failed vendor portal runs",
    description:
      "Returns up to 20 vendor portal runs from the last 30 days whose run summary flags accounts needing attention, with per-account results and the caller's unseen count.",
  }),
  /** Record that the caller opened the popover now, clearing their badge only. */
  markFailedRunsSeen: oc.output(z.object({ seenAt: z.coerce.date() })).route({
    method: "POST",
    summary: "Mark failed vendor portal runs as seen",
    description:
      "Records that the caller viewed the failed-runs list now, clearing only their own unseen badge.",
  }),
} as const;

export type VendorPortalFailedRun = z.infer<typeof VendorPortalFailedRunSchema>;
export type ListVendorPortalFailedRunsOutput = z.infer<
  typeof ListVendorPortalFailedRunsOutputSchema
>;
